import { Injectable } from '@angular/core';
import { IAppSession } from '../../core/model/appSessions/appSession';
import { Observable, forkJoin, of, EMPTY } from 'rxjs';
import { IBulkUpdateResponse, IBulkUpdateFailure } from '../../core/model/v2-api/v2-api-wrappers';
import { isNullOrUndefined } from 'util';
import { V2AppSession, BasespaceService } from '@bssh/ng-sdk';
import { AnalysisQCStatus, AnalysisStatus } from '../../core/model/appSessions/analysis-status';
import { catchError, map } from 'rxjs/operators';
import { ArrayUtilities } from '@app/core/utilities/array-utilities';

export interface IChangeAnalysisQCStatusService {
    canChangeAnalysisQCStatus(analyses: IAppSession[], userId: string): boolean;
    changeAnalysisQCStatus(analyses: IAppSession[], deliveryStatus: AnalysisQCStatus, comments?: string):
    Observable<IBulkUpdateResponse<IAppSession>>;
}

@Injectable({
  providedIn: 'root'
})

export class ChangesAnalysisQcStatusService implements IChangeAnalysisQCStatusService {

    constructor(private basespaceApi: BasespaceService) { }

    /**
     * Checks if the change delivery status featured is allowed on selected analyses.
     * @param analyses The analyses for which the check is to be made.
     */
    public canChangeAnalysisQCStatus(analyses: IAppSession[]): boolean {
        return (!ArrayUtilities.isNullOrEmpty(analyses) &&
                analyses.every(analysis => (analysis.ExecutionStatus === AnalysisStatus.Complete)));
    }


    /**
     * Changes the analyses delivery status .
     * @param analyses The array of analyses for which the delivery status has to be changed.
     * @param deliveryStatus The status to which the delivery status has to be changed to.
     * @param comments Additional comments.
     */
    public changeAnalysisQCStatus(analyses: IAppSession[], qcStatus: AnalysisQCStatus,
                                  comments?: string): Observable<IBulkUpdateResponse<IAppSession>> {
        const analysesUpdateResponse: IBulkUpdateResponse<IAppSession> = {
            SuccessItems: [],
            TotalSuccessCount: 0,
            FailureItems: [],
            TotalFailureCount: 0
        };

        const postData = {
            QcStatus: AnalysisQCStatus[qcStatus].toString(),
            ...!isNullOrUndefined(comments) && { Comment: comments }
        };

        return forkJoin(
            analyses.map((analysis: IAppSession, index: number) => {
                return this.basespaceApi.PostV2AppsessionsId({id: analysis.Id, payload: postData}).
                    pipe(
                        catchError(error => {
                            const failureObject: IBulkUpdateFailure<IAppSession> = {
                                Id: analysis.Name,
                                ErrorMessage: error.error.ErrorMessage,
                                ErrorCode: error.status
                            };
                            analysesUpdateResponse.FailureItems.push(failureObject);
                            analysesUpdateResponse.TotalFailureCount++;
                            return of(undefined);
                        }),
                    ).pipe(
                        map((response: V2AppSession) => {
                            if (response) {
                                analysesUpdateResponse.SuccessItems.push(this.toIAppSession(response));
                                analysesUpdateResponse.TotalSuccessCount++;
                            }
                            // as nothing needs to be returned from this observable, but analysesUpdateResponse needs to returned
                            return EMPTY;
                        })
                    );
            })
        ).pipe(map((response: any) => {
            return analysesUpdateResponse;
        }));
    }

    private toIAppSession(response: V2AppSession): IAppSession {
        return JSON.parse(JSON.stringify(response)) as IAppSession;
    }

}
